import { isCheckoutV2Enabled } from "../checkout-v2/domain/featureFlags";
import { getAuthCapability } from "../checkout-v2/domain/authCapability";

const ORDERPASS_RETURN_PARAMS = [
  "orderpass_authenticated",
  "orderpass_name",
  "orderpass_email",
  "orderpass_phone",
  "orderpass_storefront_token",
  "orderpass_payload",
  "orderpass_code",
];

export const normalizeOrderPassValue = (value) =>
  `${value || ""}`
    .replace(/[\u200B-\u200F\u202A-\u202E\u2060\uFEFF]/g, "")
    .trim()
    .toLowerCase();

const isLikelyNumericIdentifier = (value) => {
  if (value === null || value === undefined) return false;
  const normalizedValue = `${value}`.trim();
  return normalizedValue !== "" && /^[0-9]+$/.test(normalizedValue);
};

export const normalizeOrderPassPhone = (phone) => {
  const rawPhone = `${phone || ""}`.trim();
  if (!rawPhone) return "";

  const hasPlus = rawPhone.startsWith("+");
  const digits = rawPhone.replace(/\D/g, "");
  if (!digits) return "";

  return hasPlus ? `+${digits}` : `+${digits}`;
};

export const isOrderPassEnabled = (settings) => !!settings?.enable_orderpass;

export const hasOrderPassContact = (customerContact) =>
  !!customerContact?.name || !!customerContact?.phone || !!customerContact?.email;

export const isOrderPassPhoneVerified = (customerContact, verifiedPhone) => {
  const currentPhone = normalizeOrderPassPhone(customerContact?.phone);
  const normalizedVerifiedPhone = normalizeOrderPassPhone(verifiedPhone);

  return !!currentPhone && !!normalizedVerifiedPhone && currentPhone === normalizedVerifiedPhone;
};

export const isOrderPassGuestBypassed = (customerContact, guestBypassPhone) => {
  const currentPhone = normalizeOrderPassPhone(customerContact?.phone);
  const normalizedGuestBypassPhone = normalizeOrderPassPhone(guestBypassPhone);

  return (
    !!currentPhone &&
    !!normalizedGuestBypassPhone &&
    currentPhone === normalizedGuestBypassPhone
  );
};

export const shouldRequireOrderPassVerification = (
  settings,
  customerContact,
  verifiedPhone,
  guestBypassPhone,
) =>
  isOrderPassEnabled(settings) &&
  !isOrderPassPhoneVerified(customerContact, verifiedPhone) &&
  !isOrderPassGuestBypassed(customerContact, guestBypassPhone);

export const shouldStartWithOrderPassContact = (
  settings,
  customerContact,
  verifiedPhone,
  guestBypassPhone,
) =>
  shouldRequireOrderPassVerification(
    settings,
    customerContact,
    verifiedPhone,
    guestBypassPhone,
  );

export const canAddToCartBeforeBranchSelection = (settings) =>
  isOrderPassEnabled(settings);

// Let the v2 shell resolve the first incomplete section. Legacy confirmation
// routes intentionally carry a final-step hint, which Buy now must not reuse.
export const getBuyNowCheckoutRoute = (settings, customer = {}) =>
  isCheckoutV2Enabled(settings)
    ? "/checkout"
    : shouldStartWithOrderPassContact(
        settings,
        customer?.customerContact,
        customer?.orderPassVerifiedPhone,
        customer?.orderPassGuestBypassPhone,
      ) ? "/checkout/details" : "/checkout/confirmation";

export const isDeliveryServiceAvailable = ({ settings, cart }) =>
  !!(settings?.enable_delivery || settings?.enable_delivery_select) &&
  !(cart?.pickup_only_items > 0);

export const isPickupServiceAvailable = ({ settings, branches, cart }) =>
  !!(
    settings?.enable_pickup ||
    settings?.enable_pickup_select ||
    settings?.enable_resource_based_booking
  ) &&
  !!branches?.filter((branch) => branch.enable_pickup)?.length &&
  !(cart?.delivery_only_items > 0);

export const canUseSocialAuth = (settings) =>
  !isOrderPassEnabled(settings) && (isCheckoutV2Enabled(settings)
    ? getAuthCapability({ settings }).enabled
    : !!settings?.enable_social_auth && !!settings?.enable_social_auth_staff);

export const shouldShowProfileMenu = (settings, hasMenuPages = false) =>
  canUseSocialAuth(settings) || isOrderPassEnabled(settings) || hasMenuPages;

export const getSavedAddresses = (auth, orderPassAddresses = [], settings = null) =>
  auth?.user
    ? auth?.addresses || []
    : isOrderPassEnabled(settings)
      ? orderPassAddresses || []
      : [];

const sanitizeDomain = (value) =>
  `${value || ""}`
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "");

export const getOrderPassDomain = (settings, store) =>
  [
    settings?.domain,
    settings?.store_domain,
    settings?.custom_domain,
    store?.domain,
    store?.alt_domain,
  ]
    .map(sanitizeDomain)
    .find(Boolean) || null;

export const getOrderPassAccountBaseUrl = () => {
  const isDevelopment =
    process.env.NODE_ENV === "development" ||
    (typeof window !== "undefined" &&
      ["localhost", "127.0.0.1"].includes(window.location.hostname));

  return (
    isDevelopment
      ? "http://localhost:3003"
      : "https://account.orderpass.me"
  ).replace(/\/+$/, "");
};

export const buildOrderPassRedirectUrl = ({
  settings,
  store,
  phone,
  returnUrl,
}) => {
  const accountBaseUrl = getOrderPassAccountBaseUrl(settings);
  const redirectUrl = new URL(accountBaseUrl);

  if (returnUrl) {
    redirectUrl.searchParams.set("return_url", returnUrl);
  }

  const normalizedPhone = normalizeOrderPassPhone(phone);
  if (normalizedPhone) {
    redirectUrl.searchParams.set("phone_number", normalizedPhone);
  }

  const domain = getOrderPassDomain(settings, store);
  if (domain) {
    redirectUrl.searchParams.set("domain", domain);
  }

  return redirectUrl.toString();
};

export const getOrderPassReturnData = (search) => {
  const params = new URLSearchParams(search || "");
  if (params.get("orderpass_authenticated") !== "1") return null;

  return {
    authenticated: true,
    redirectCode: `${params.get("orderpass_code") || ""}`.trim(),
  };
};

export const clearOrderPassReturnParamsFromSearch = (search) => {
  const params = new URLSearchParams(search || "");
  ORDERPASS_RETURN_PARAMS.forEach((param) => params.delete(param));
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

export const hasOrderPassData = (data) => {
  const addresses = Array.isArray(data?.addresses) ? data.addresses : [];
  return Boolean(
    `${data?.name || ""}`.trim() ||
      `${data?.email || ""}`.trim() ||
      addresses.length,
  );
};

const parseCoordinate = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getAreaNameCandidates = (source) =>
  [
    source?.area,
    source?.area_name,
    source?.area__name,
    source?.area_ar,
    source?.area__ar_name,
    source?.delivery_charge__area__name,
    source?.delivery_charge__area__ar_name,
    source?.name,
    source?.ar_name,
  ]
    .concat(
      typeof source?.area === "object"
        ? [source?.area?.name, source?.area?.ar_name]
        : [],
    )
    .map(normalizeOrderPassValue)
    .filter(Boolean);

export const getOrderPassSavedAreaNames = (addresses = []) => {
  const savedAreaNames = [];
  const seenAreaNames = new Set();

  (addresses || []).forEach((address) => {
    [
      address?.area,
      address?.area_name,
      address?.area__name,
      address?.area_ar,
      address?.area__ar_name,
      address?.delivery_charge__area__name,
      address?.delivery_charge__area__ar_name,
      address?.name,
      address?.ar_name,
      typeof address?.area === "object" ? address?.area?.name : null,
      typeof address?.area === "object" ? address?.area?.ar_name : null,
    ]
      .map((value) => `${value || ""}`.trim())
      .filter(Boolean)
      .forEach((value) => {
        const normalizedValue = normalizeOrderPassValue(value);
        if (!normalizedValue || seenAreaNames.has(normalizedValue)) return;
        seenAreaNames.add(normalizedValue);
        savedAreaNames.push(value);
      });
  });

  return savedAreaNames;
};

const findMatchingArea = ({ address, settings, areas, branchDeliveryCharges }) => {
  const areaNameCandidates = getAreaNameCandidates(address);
  if (!areaNameCandidates.length) return null;

  if (settings?.is_branch_based_ordering) {
    const match = (branchDeliveryCharges || []).find((bdc) =>
      getAreaNameCandidates(bdc).some((value) => areaNameCandidates.includes(value)),
    );

    if (!match) return null;

    return {
      area_id: match.area_id,
      area__name: match.area,
      area__ar_name: match.area_ar,
      country_id: match.country_id,
      branch_delivery_charge_id: match.id,
      branch_delivery_charge__branch__id: match.branch,
      branch_delivery_charge__branch__name: match.branch_name,
      branch_delivery_charge__branch__ar_name: match.branch_name_ar,
    };
  }

  const match = (areas || []).find((area) =>
    getAreaNameCandidates(area).some((value) => areaNameCandidates.includes(value)),
  );

  if (!match) return null;

  return {
    area_id: match.id,
    area__name: match.name,
    area__ar_name: match.ar_name,
    country_id: match.country,
  };
};

export const hydrateOrderPassAddresses = (
  addresses,
  { settings, areas, branchDeliveryCharges, countries },
) =>
  (addresses || []).map((address, index) => {
    const areaMatch = findMatchingArea({
      address,
      settings,
      areas,
      branchDeliveryCharges,
    });
    const country = (countries || []).find((item) => item.id === areaMatch?.country_id);
    const lat = parseCoordinate(address.lat);
    const lng = parseCoordinate(address.lng);

    return {
      id: `orderpass-${address.id || index}`,
      orderPass: true,
      orderPassAddressId: address.id || null,
      orderPassMatched: !!areaMatch,
      is_default: !!address.is_default,
      order_count: Number(address.order_count || address.count || 0) || 0,
      is_preferred: index === 0,
      area__is_deleted: !areaMatch,
      area__delivery_charge__is_active: !!areaMatch,
      area__delivery_charge__is_deleted: !areaMatch,
      branch_delivery_charge__is_active: !!areaMatch,
      branch_delivery_charge__is_deleted: !areaMatch,
      branch_delivery_charge__branch__is_deleted: false,
      area__name: areaMatch?.area__name || address.area || address.area_name || "",
      area__ar_name: areaMatch?.area__ar_name || "",
      area_id: areaMatch?.area_id || null,
      country_id: areaMatch?.country_id || null,
      country__name:
        country?.name || address.country_name || address.country || "",
      branch_delivery_charge_id: areaMatch?.branch_delivery_charge_id || null,
      branch_delivery_charge__branch__id:
        areaMatch?.branch_delivery_charge__branch__id || null,
      branch_delivery_charge__branch__name:
        areaMatch?.branch_delivery_charge__branch__name || "",
      branch_delivery_charge__branch__ar_name:
        areaMatch?.branch_delivery_charge__branch__ar_name || "",
      block: address.block || "",
      zone: address.zone || "",
      street: address.street || "",
      building: address.building || "",
      avenue: address.avenue || "",
      floor: address.floor || "",
      apartment: address.apartment || "",
      additional: address.additional || "",
      lat,
      lng,
      gpsConfirmed: lat !== null && lng !== null,
      gps_confirmed: lat !== null && lng !== null,
      location_id: address.paci || address.location_id || "",
      checkoutAddress: areaMatch
        ? {
            orderPassAddressId: address.id || null,
            selectedDeliveryType: 0,
            selectedAddressType:
              address.apartment || address.floor ? 1 : 0,
            pickupArea: null,
            gpsConfirmed: lat !== null && lng !== null,
            country: areaMatch.country_id,
            area: areaMatch.area_id,
            block: address.block || "",
            street: address.street || "",
            building: address.building || "",
            avenue: address.avenue || "",
            apartment: address.apartment || "",
            floor: address.floor || "",
            additional: address.additional || "",
            lat,
            lng,
            paci: address.paci || address.location_id || "",
          }
        : null,
    };
  });

const getCountryNameFromId = (countryId, countries = []) =>
  countries.find((country) => country.id == countryId)?.name || "";

const getAreaFromContext = (address, settings, areas, branchDeliveryCharges) => {
  if (!address?.area) return null;

  if (settings?.is_branch_based_ordering) {
    const bdc = (branchDeliveryCharges || []).find(
      (item) => item.area_id == address.area || item.id == address.area,
    );
    if (bdc) {
      return {
        name: bdc.area || "",
        country_id: bdc.country_id || null,
      };
    }
  }

  const area = (areas || []).find((item) => item.id == address.area);
  if (!area) return null;

  return {
    name: area.name || "",
    country_id: area.country || null,
  };
};

const mapSavedAddressForOrderPassSync = (address, context) => {
  if (!address) return null;

  const areaFromContext = getAreaFromContext(
    address,
    context.settings,
    context.areas,
    context.branchDeliveryCharges,
  );
  const countryName =
    address.country__name ||
    address.country_name ||
    getCountryNameFromId(
      address.country_id || areaFromContext?.country_id,
      context.countries,
    );
  const rawAreaName =
    address.area__name ||
    address.area_name ||
    areaFromContext?.name ||
    "";
  const areaName =
    !isLikelyNumericIdentifier(rawAreaName) && rawAreaName
      ? rawAreaName
      : !isLikelyNumericIdentifier(areaFromContext?.name)
        ? areaFromContext?.name || ""
        : "";

  const mappedAddress = {
    label: address.name || address.label || "",
    country_name: countryName,
    area_name: areaName,
    block: address.block || "",
    zone: address.zone || "",
    street: address.street || "",
    building: address.building || "",
    avenue: address.avenue || "",
    floor: address.floor || "",
    apartment: address.apartment || "",
    additional: address.additional || "",
    postal_code: address.postal_code || "",
    lat: address.lat || "",
    lng: address.lng || "",
    location_id: address.location_id || address.paci || "",
  };

  const hasUsefulData = Object.values(mappedAddress).some(
    (value) => `${value || ""}`.trim() !== "",
  );

  return hasUsefulData ? mappedAddress : null;
};

export const getStorefrontOrderPassAddressData = ({
  address,
  settings,
  areas,
  branchDeliveryCharges,
  countries,
}) =>
  mapSavedAddressForOrderPassSync(address, {
    settings,
    areas,
    branchDeliveryCharges,
    countries,
  });

const dedupeSyncAddresses = (addresses = []) => {
  const seen = new Set();

  return addresses.filter((address) => {
    const key = [
      normalizeOrderPassValue(address.area_name),
      normalizeOrderPassValue(address.block),
      normalizeOrderPassValue(address.street),
      normalizeOrderPassValue(address.building),
      normalizeOrderPassValue(address.avenue),
      normalizeOrderPassValue(address.floor),
      normalizeOrderPassValue(address.apartment),
      normalizeOrderPassValue(address.lat),
      normalizeOrderPassValue(address.lng),
    ].join("|");

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeAddressFingerprintValue = (value) =>
  normalizeOrderPassValue(value)
    .replace(/\bstreet\b/g, "st")
    .replace(/[^\w]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildOrderPassAddressFingerprint = (address = {}) =>
  [
    address.country_name || address.country,
    address.area_name || address.area,
    address.zone,
    address.block,
    address.street,
    address.avenue,
    address.building,
    address.floor,
    address.apartment,
  ]
    .map(normalizeAddressFingerprintValue)
    .join("|");

const hasMissingLocalOrderPassAddress = (localAddresses = [], returnedAddresses = []) => {
  const returnedFingerprints = new Set(
    (returnedAddresses || [])
      .map(buildOrderPassAddressFingerprint)
      .filter((fingerprint) => fingerprint.replace(/\|/g, "")),
  );

  return (localAddresses || []).some((address) => {
    const fingerprint = buildOrderPassAddressFingerprint(address);
    return !!fingerprint.replace(/\|/g, "") && !returnedFingerprints.has(fingerprint);
  });
};

export const getStorefrontOrderPassSyncData = ({
  customerContact,
  address,
  authAddresses,
  settings,
  areas,
  branchDeliveryCharges,
  countries,
}) => {
  const context = {
    settings,
    areas,
    branchDeliveryCharges,
    countries,
  };

  const addresses = dedupeSyncAddresses(
    []
      .concat(authAddresses || [])
      .concat(address ? [address] : [])
      .map((savedAddress) => mapSavedAddressForOrderPassSync(savedAddress, context))
      .filter(Boolean),
  );

  return {
    name: `${customerContact?.name || ""}`.trim(),
    email: `${customerContact?.email || ""}`.trim(),
    phone: normalizeOrderPassPhone(customerContact?.phone),
    addresses,
  };
};

export const shouldSyncStorefrontOrderPassData = ({
  returnedData,
  localData,
}) => {
  if (!localData?.phone) return false;

  const returnedAddresses = Array.isArray(returnedData?.addresses)
    ? returnedData.addresses
    : [];
  const localAddresses = Array.isArray(localData?.addresses)
    ? localData.addresses
    : [];

  return Boolean(
    (localData.name && !returnedData?.name) ||
      (localData.email && !returnedData?.email) ||
      hasMissingLocalOrderPassAddress(localAddresses, returnedAddresses),
  );
};
